export interface ChecklistItem {
  id: string;
  text: string;
  order: number;
}

export interface ChecklistSubsection {
  title: string;
  items: ChecklistItem[];
}

export interface ChecklistSection {
  category: string;
  subsections: ChecklistSubsection[];
}

export const INSPECTION_CHECKLIST: ChecklistSection[] = [
  {
    category: 'EXTERIOR',
    subsections: [
      {
        title: 'Entry & Curb Appeal',
        items: [
          { id: 'ext1', text: 'Front walkway clear and clean', order: 1 },
          { id: 'ext2', text: 'Porch/entry swept and welcoming', order: 2 },
          { id: 'ext3', text: 'House numbers visible', order: 3 },
          { id: 'ext4', text: 'Exterior lighting functional', order: 4 },
          { id: 'ext5', text: 'Door locks working smoothly', order: 5 },
          { id: 'ext6', text: 'Welcome mat present and clean', order: 6 },
          { id: 'ext7', text: 'Fingerprints and spots wiped from entry door and hardware', order: 7 },
        ],
      },
      {
        title: 'Landscaping & Grounds',
        items: [
          { id: 'land1', text: 'Lawn mowed/maintained', order: 8 },
          { id: 'land2', text: 'Walkways clear of debris', order: 9 },
          { id: 'land3', text: 'Plants/shrubs trimmed', order: 10 },
          { id: 'land4', text: 'Weeds removed', order: 11 },
          { id: 'land5', text: 'Outdoor furniture clean and arranged', order: 12 },
          { id: 'land6', text: 'Hoses coiled, tools stored', order: 13 },
          { id: 'land7', text: 'Window wells cleaned if necessary', order: 14 },
          { id: 'land8', text: 'Landscaping maintenance needs noted', order: 15 },
        ],
      },
      {
        title: 'Outdoor Spaces',
        items: [
          { id: 'out1', text: 'Deck/patio swept and clean', order: 16 },
          { id: 'out2', text: 'Bird droppings washed off with hot water', order: 17 },
          { id: 'out3', text: 'Grill cleaned (if applicable)', order: 18 },
          { id: 'out4', text: 'Fire tables cleaned (if applicable)', order: 19 },
          { id: 'out5', text: 'Propane tanks checked and refilled/replaced if empty', order: 20 },
          { id: 'out6', text: 'Outdoor cushions clean and dry', order: 21 },
          { id: 'out7', text: 'Hot tub/pool clean and operational (if applicable)', order: 22 },
          { id: 'out8', text: 'Outdoor trash removed', order: 23 },
          { id: 'out9', text: 'Trash/recycling bins empty and stored properly', order: 24 },
        ],
      },
      {
        title: 'Building Exterior',
        items: [
          { id: 'bld1', text: 'Gutters clear of debris', order: 25 },
          { id: 'bld2', text: 'No visible damage to siding/paint', order: 26 },
          { id: 'bld3', text: 'Windows clean (exterior)', order: 27 },
          { id: 'bld4', text: 'Garage door functional', order: 28 },
          { id: 'bld5', text: 'No safety hazards present', order: 29 },
        ],
      },
    ],
  },
  {
    category: 'INTERIOR - LIVING AREAS',
    subsections: [
      {
        title: 'Entry/Foyer',
        items: [
          { id: 'foyer1', text: 'Floors clean and vacuumed/mopped', order: 30 },
          { id: 'foyer2', text: 'Closet organized, hangers available', order: 31 },
          { id: 'foyer3', text: 'Hooks/coat rack functional', order: 32 },
          { id: 'foyer4', text: 'Shoe mat present', order: 33 },
        ],
      },
      {
        title: 'Living Room',
        items: [
          { id: 'liv1', text: 'Floors vacuumed/mopped, no hairs visible', order: 34 },
          { id: 'liv2', text: 'Furniture clean and arranged properly', order: 35 },
          { id: 'liv3', text: 'Cushions fluffed and arranged', order: 36 },
          { id: 'liv4', text: 'Coffee table clean and styled', order: 37 },
          { id: 'liv5', text: 'TV and remote functional (batteries checked)', order: 38 },
          { id: 'liv6', text: 'Streaming services working', order: 39 },
          { id: 'liv7', text: 'Wi-Fi password displayed/available', order: 40 },
          { id: 'liv8', text: 'Lamps and light switches working', order: 41 },
          { id: 'liv9', text: 'Spider webs/cobwebs removed from ceilings and corners', order: 42 },
          { id: 'liv10', text: 'Light fixtures clean and dust-free', order: 43 },
          { id: 'liv11', text: 'Windows clean (interior)', order: 44 },
          { id: 'liv12', text: 'Curtains clean and functional', order: 45 },
          { id: 'liv13', text: 'Blinds/curtains functional and clean', order: 46 },
          { id: 'liv14', text: 'Décor dusted and in place', order: 47 },
          { id: 'liv15', text: 'Fingerprints and spots wiped from walls, doors, and switches', order: 48 },
          { id: 'liv16', text: 'Vents dusted and clean', order: 49 },
          { id: 'liv17', text: 'No odors present', order: 50 },
          { id: 'liv18', text: 'Temperature comfortable', order: 51 },
        ],
      },
      {
        title: 'Dining Area',
        items: [
          { id: 'din1', text: 'Table and chairs clean', order: 52 },
          { id: 'din2', text: 'Place settings available (if provided)', order: 53 },
          { id: 'din3', text: 'Centerpiece/décor in place', order: 54 },
        ],
      },
    ],
  },
  {
    category: 'KITCHEN',
    subsections: [
      {
        title: 'Appliances',
        items: [
          { id: 'kit1', text: 'Refrigerator clean inside/out, cooling properly', order: 55 },
          { id: 'kit2', text: 'Freezer clean, ice maker working', order: 56 },
          { id: 'kit3', text: 'Oven/stove clean and functional, no stuck-on food or grunge', order: 57 },
          { id: 'kit4', text: 'Microwave clean inside/out, functional', order: 58 },
          { id: 'kit5', text: 'Dishwasher clean and functional', order: 59 },
          { id: 'kit6', text: 'Coffee maker clean and functional, no old filters left inside', order: 60 },
          { id: 'kit7', text: 'Toaster/small appliances clean', order: 61 },
        ],
      },
      {
        title: 'Cabinets & Pantry',
        items: [
          { id: 'cab1', text: 'Dishes clean and organized (check for chips/cracks)', order: 62 },
          { id: 'cab2', text: 'Glassware clean and complete set', order: 63 },
          { id: 'cab3', text: 'Pots/pans clean, organized, and not peeling', order: 64 },
          { id: 'cab4', text: 'Utensils complete and clean', order: 65 },
          { id: 'cab5', text: 'No extra trash or clutter in cabinets or drawers', order: 66 },
          { id: 'cab6', text: 'Basic pantry items stocked (salt, pepper, oil, coffee, etc.)', order: 67 },
          { id: 'cab7', text: 'Condiments, oils, and seasonings checked for expiration dates', order: 68 },
        ],
      },
      {
        title: 'Kitchen Surfaces',
        items: [
          { id: 'surf1', text: 'Countertops clean and clear', order: 69 },
          { id: 'surf2', text: 'Sink clean and draining properly', order: 70 },
          { id: 'surf3', text: 'Faucet functional, no leaks', order: 71 },
          { id: 'surf4', text: 'Drains treated with vinegar/baking soda if stale odors present', order: 72 },
          { id: 'surf5', text: 'Cabinet doors and hardware functional', order: 73 },
          { id: 'surf6', text: 'Floors clean and mopped', order: 74 },
          { id: 'surf7', text: 'Trash can empty with fresh liner', order: 75 },
          { id: 'surf8', text: 'Dish soap and sponges available', order: 76 },
          { id: 'surf9', text: 'Dish towels clean and folded', order: 77 },
          { id: 'surf10', text: 'Paper towels well stocked', order: 78 },
          { id: 'surf11', text: 'Table/bar stools clean', order: 79 },
          { id: 'surf12', text: 'Fingerprints and spots wiped from walls, doors, and switches', order: 80 },
        ],
      },
    ],
  },
  {
    category: 'BEDROOMS',
    subsections: [
      {
        title: 'Master Bedroom',
        items: [
          { id: 'bed1', text: 'Bed made with fresh linens (check for stains or wear)', order: 81 },
          { id: 'bed2', text: 'Mattress protector in place', order: 82 },
          { id: 'bed3', text: 'Pillows fluffed (check quantity)', order: 83 },
          { id: 'bed4', text: 'Extra blankets available', order: 84 },
          { id: 'bed5', text: 'Nightstands clean and styled', order: 85 },
          { id: 'bed6', text: 'Lamps functional', order: 86 },
          { id: 'bed7', text: 'TV remote functional with batteries checked', order: 87 },
          { id: 'bed8', text: 'Closet empty with hangers', order: 88 },
          { id: 'bed9', text: 'Dresser drawers empty and clean', order: 89 },
          { id: 'bed10', text: 'Floors vacuumed, no hairs visible', order: 90 },
          { id: 'bed11', text: 'Windows/mirrors clean', order: 91 },
          { id: 'bed12', text: 'Spider webs/cobwebs removed from ceilings and corners', order: 92 },
          { id: 'bed13', text: 'Fingerprints and spots wiped from walls, doors, and switches', order: 93 },
          { id: 'bed14', text: 'Vents dusted and clean', order: 94 },
          { id: 'bed15', text: 'Outlets functional', order: 95 },
          { id: 'bed16', text: 'Door and locks functional', order: 96 },
        ],
      },
      {
        title: 'Additional Bedrooms',
        items: [
          { id: 'addbed1', text: 'Repeat all master bedroom items for each room', order: 97 },
        ],
      },
    ],
  },
  {
    category: 'BATHROOMS',
    subsections: [
      {
        title: 'Master Bathroom',
        items: [
          { id: 'bath1', text: 'Toilet clean inside/out, flushing properly', order: 98 },
          { id: 'bath2', text: 'Sink clean, faucet functional', order: 99 },
          { id: 'bath3', text: 'Shower/tub clean, no mold/mildew, no hairs visible', order: 100 },
          { id: 'bath4', text: 'Shower head functional, good pressure', order: 101 },
          { id: 'bath5', text: 'Drain flowing properly, treat with vinegar/baking soda if stale odors', order: 102 },
          { id: 'bath6', text: 'Fresh towels hung (check for stains or wear)', order: 103 },
          { id: 'bath7', text: 'Bath mat clean and in place', order: 104 },
          { id: 'bath8', text: 'Toiletries stocked (toilet paper, soap, shampoo)', order: 105 },
          { id: 'bath9', text: 'Body product refills adequate (shampoo, conditioner, body wash)', order: 106 },
          { id: 'bath10', text: 'Mirror clean and streak-free', order: 107 },
          { id: 'bath11', text: 'Floors mopped', order: 108 },
          { id: 'bath12', text: 'Trash can empty with liner', order: 109 },
          { id: 'bath13', text: 'Exhaust fan working', order: 110 },
          { id: 'bath14', text: 'Cabinet/drawer clean and organized', order: 111 },
          { id: 'bath15', text: 'Spider webs/cobwebs removed from corners', order: 112 },
          { id: 'bath16', text: 'Fingerprints and spots wiped from walls, doors, and switches', order: 113 },
          { id: 'bath17', text: 'Vents dusted and clean', order: 114 },
        ],
      },
      {
        title: 'Additional Bathrooms',
        items: [
          { id: 'addbath1', text: 'Repeat all master bathroom items for each room', order: 115 },
        ],
      },
    ],
  },
  {
    category: 'UTILITIES & SYSTEMS',
    subsections: [
      {
        title: 'Heating/Cooling',
        items: [
          { id: 'hvac1', text: 'Thermostat functional and set appropriately', order: 116 },
          { id: 'hvac2', text: 'HVAC system operational', order: 117 },
          { id: 'hvac3', text: 'Filters clean (note if replacement needed)', order: 118 },
          { id: 'hvac4', text: 'Vents unobstructed', order: 119 },
        ],
      },
      {
        title: 'Technology',
        items: [
          { id: 'tech1', text: 'Wi-Fi working throughout property', order: 120 },
          { id: 'tech2', text: 'Smart lock functioning and synced', order: 121 },
          { id: 'tech3', text: 'All TVs operational', order: 122 },
          { id: 'tech4', text: 'Remote controls present with batteries', order: 123 },
          { id: 'tech5', text: 'Streaming accounts logged in', order: 124 },
          { id: 'tech6', text: 'Smart home devices functional', order: 125 },
        ],
      },
      {
        title: 'Safety & Security',
        items: [
          { id: 'safe1', text: 'Smoke detectors functional, batteries checked, not expired', order: 126 },
          { id: 'safe2', text: 'Carbon monoxide detectors functional, batteries checked', order: 127 },
          { id: 'safe3', text: 'Fire extinguisher present and charged', order: 128 },
          { id: 'safe4', text: 'First aid kit stocked', order: 129 },
          { id: 'safe5', text: 'All door locks functional', order: 130 },
          { id: 'safe6', text: 'Keyless entry locks functional, replace batteries if needed', order: 131 },
          { id: 'safe7', text: 'Doorbell and exterior cameras are functional, replace batteries if needed', order: 132 },
          { id: 'safe8', text: 'Window locks functional', order: 133 },
          { id: 'safe9', text: 'Exterior security lights working', order: 134 },
        ],
      },
    ],
  },
  {
    category: 'SUPPLIES & AMENITIES',
    subsections: [
      {
        title: 'Linens',
        items: [
          { id: 'lin1', text: 'Bed linens - complete sets for all beds', order: 135 },
          { id: 'lin2', text: 'Bath towels - adequate quantity', order: 136 },
          { id: 'lin3', text: 'Hand towels - adequate quantity', order: 137 },
          { id: 'lin4', text: 'Washcloths - adequate quantity', order: 138 },
          { id: 'lin5', text: 'Kitchen towels - clean supply', order: 139 },
        ],
      },
      {
        title: 'Paper Goods & Toiletries',
        items: [
          { id: 'paper1', text: 'Toilet paper - at least 2 rolls per bathroom', order: 140 },
          { id: 'paper2', text: 'Paper towels - at least 1 roll', order: 141 },
          { id: 'paper3', text: 'Tissues available', order: 142 },
          { id: 'paper4', text: 'Hand soap in all bathrooms', order: 143 },
          { id: 'paper5', text: 'Dish soap in kitchen', order: 144 },
          { id: 'paper6', text: 'Laundry detergent (if washer available)', order: 145 },
          { id: 'paper7', text: 'Trash bags - various sizes available', order: 146 },
          { id: 'paper8', text: 'Shampoo/conditioner starter supply', order: 147 },
          { id: 'paper9', text: 'Body wash starter supply', order: 148 },
        ],
      },
      {
        title: 'Cleaning Supplies (Guest Use)',
        items: [
          { id: 'clean1', text: 'Vacuum accessible', order: 149 },
          { id: 'clean2', text: 'Broom and dustpan', order: 150 },
          { id: 'clean3', text: 'Mop and bucket', order: 151 },
          { id: 'clean4', text: 'Basic cleaning supplies under sink', order: 152 },
        ],
      },
    ],
  },
  {
    category: 'LAUNDRY',
    subsections: [
      {
        title: 'Laundry (if applicable)',
        items: [
          { id: 'laun1', text: 'Washer clean and functional', order: 153 },
          { id: 'laun2', text: 'Dryer clean, lint trap empty, functional', order: 154 },
          { id: 'laun3', text: 'Detergent available', order: 155 },
          { id: 'laun4', text: 'Ironing board and iron available', order: 156 },
        ],
      },
    ],
  },
  {
    category: 'DOCUMENTATION & INFO',
    subsections: [
      {
        title: 'Documentation & Info',
        items: [
          { id: 'doc1', text: 'House manual/binder present and updated', order: 157 },
          { id: 'doc2', text: 'Wi-Fi info clearly displayed', order: 158 },
          { id: 'doc3', text: 'Emergency contact info visible', order: 159 },
          { id: 'doc4', text: 'Check-out instructions clear', order: 160 },
          { id: 'doc5', text: 'Local recommendations provided', order: 161 },
          { id: 'doc6', text: 'Parking instructions clear', order: 162 },
          { id: 'doc7', text: 'Trash/recycling schedule posted', order: 163 },
        ],
      },
    ],
  },
  {
    category: 'FINAL CHECKS',
    subsections: [
      {
        title: 'Final Checks',
        items: [
          { id: 'final1', text: 'All lights turned off except welcome lights', order: 164 },
          { id: 'final2', text: 'Thermostat set appropriately', order: 165 },
          { id: 'final3', text: 'All windows closed and locked', order: 166 },
          { id: 'final4', text: 'All doors locked', order: 167 },
          { id: 'final5', text: 'No odors throughout property', order: 168 },
          { id: 'final6', text: "Owner's mail collected (if applicable)", order: 169 },
          { id: 'final7', text: 'Property photos taken (if issues noted)', order: 170 },
          { id: 'final8', text: 'Overall property ready for guest arrival', order: 171 },
        ],
      },
    ],
  },
];
